import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import { gql, useMutation } from "@apollo/client";

import EditOrgMutation from "../organization/mutations/EditOrg";
import { GlobalStyles } from "../../constants/styles";
import FilePicker from "../common/FilePicker";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const UPDATE_TEAM_LOGO = gql`
    mutation UpdateTeamLogo($teamId: ID!, $file: Upload!, $seasonId: ID) {
        updateTeamLogo(teamId: $teamId, file: $file, seasonId: $seasonId) {
            success
            errors
            team {
                id
                logoUrl
            }
        }
    }
`;

export default function EditTeamModal({ teams, setTeams, activeSeasonData }) {
    const colors = useThemeColors();
    const { t } = useTranslation();
    const [editOrgMutation] = EditOrgMutation();
    const [updateTeamLogo] = useMutation(UPDATE_TEAM_LOGO);

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    const scopeId = useMemo(
        () => `kbola-edit-team-${Math.random().toString(36).slice(2, 9)}`,
        []
    );

    useEffect(() => {
        if (!logoFile) {
            setLogoPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const clearLogo = useCallback(() => {
        setLogoFile(null);
        setLogoPreviewUrl(null);
    }, []);

    const modalStyles = useMemo(() => {
        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
                background: "#fff",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                color: colors.textOnPrimary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: { padding: 14, background: "#fff" },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: "rgba(20,21,26,0.78)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 800,
                outline: "none",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const forceUnlockPage = () => {
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("overflow");
    };

    const hideModal = () => {
        const el = document.getElementById("edit-team-modal");
        if (!el) return;

        try {
            if (window.$ && typeof window.$(el).modal === "function") {
                window.$(el).modal("hide");
                setTimeout(forceUnlockPage, 0);
                setTimeout(forceUnlockPage, 150);
                return;
            }
        } catch (_) {}

        try {
            const Modal = window.bootstrap?.Modal;
            if (Modal) {
                const instance =
                    (typeof Modal.getInstance === "function" && Modal.getInstance(el)) ||
                    (typeof Modal.getOrCreateInstance === "function" && Modal.getOrCreateInstance(el)) ||
                    new Modal(el);

                instance?.hide?.();
                setTimeout(forceUnlockPage, 0);
                setTimeout(forceUnlockPage, 150);
                return;
            }
        } catch (_) {}

        el.classList.remove("show");
        el.style.display = "none";
        el.setAttribute("aria-hidden", "true");
        el.removeAttribute("aria-modal");

        setTimeout(forceUnlockPage, 0);
        setTimeout(forceUnlockPage, 150);
    };

    return (
        <>
            <div
                id="edit-team-modal"
                className="modal fade"
                tabIndex="-1"
                role="dialog"
                aria-hidden="true"
            >
                <div className="modal-dialog" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <Form
                            id="editTeam"
                            method="post"
                            onSubmit={async (e) => {
                                e.preventDefault();

                                const formElements = e.currentTarget.elements;
                                const teamId = formElements.namedItem("id").value;
                                const newName = formElements.namedItem("name").value;

                                if (!teamId) {
                                    toast.error(t("editTeamModal.missingTeamId"), {
                                        autoClose: 3000,
                                        toastId: "team_edit_missing_id",
                                        position: "top-center",
                                        closeOnClick: true,
                                    });
                                    return;
                                }

                                try {
                                    const res = await editOrgMutation({
                                        variables: {
                                            name: newName,
                                            orgId: teamId,
                                            organizationType: "team",
                                        },
                                    });

                                    const editedOrg = res?.data?.editTeam?.organization;

                                    if (!editedOrg) return;

                                    let finalLogoUrl = editedOrg.logoUrl ?? null;

                                    if (logoFile) {
                                        try {
                                            const up = await updateTeamLogo({
                                                variables: {
                                                    teamId,
                                                    file: logoFile,
                                                    seasonId: activeSeasonData?.id ?? null,
                                                },
                                            });

                                            const payload = up?.data?.updateTeamLogo;

                                            if (!payload?.success) {
                                                toast.warn(
                                                    t("editTeamModal.teamUpdatedLogoFailed", {
                                                        errors: (payload?.errors || []).join(", "),
                                                    }),
                                                    {
                                                        autoClose: 4500,
                                                        toastId: "team_logo_failed_edit",
                                                        position: "top-center",
                                                    }
                                                );
                                            } else {
                                                finalLogoUrl = payload?.team?.logoUrl ?? finalLogoUrl;
                                                clearLogo();
                                            }
                                        } catch (err) {
                                            toast.warn(
                                                t("editTeamModal.teamUpdatedLogoFailed", {
                                                    errors: err.message,
                                                }),
                                                {
                                                    autoClose: 4500,
                                                    toastId: "team_logo_failed_edit",
                                                    position: "top-center",
                                                }
                                            );
                                        }
                                    }

                                    setTeams((prev) =>
                                        prev.map((tItem) => {
                                            const same =
                                                String(tItem.teamId) === String(teamId) ||
                                                String(tItem?.team?.id) === String(teamId);

                                            if (!same) return tItem;

                                            return {
                                                ...tItem,
                                                teamName: newName,
                                                team: {
                                                    ...tItem.team,
                                                    logoUrl: finalLogoUrl ?? tItem?.team?.logoUrl ?? null,
                                                },
                                            };
                                        })
                                    );

                                    toast.success(t("editTeamModal.teamEditedSuccess"), {
                                        autoClose: 3000,
                                        toastId: "team_edited",
                                        position: "top-center",
                                        closeOnClick: true,
                                    });

                                    e.target.reset();
                                    hideModal();
                                } catch (err) {
                                    toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                                        autoClose: 3000,
                                        toastId: "team_edit_error",
                                        position: "top-center",
                                        closeOnClick: true,
                                    });
                                }
                            }}
                        >
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>{t("editTeamModal.badge")}</span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("editTeamModal.title")}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={hideModal}
                                    aria-label={t("editTeamModal.closeAria")}
                                    style={modalStyles.closeBtn}
                                    title={t("editTeamModal.closeTitle")}
                                >
                                    <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editTeamModal.sectionTitle")}
                                    </div>

                                    <input type="hidden" name="id" id="edit-org-id" />

                                    <FilePicker
                                        scopeId={scopeId}
                                        colors={colors}
                                        label={t("editTeamModal.logoLabel")}
                                        hint={t("editTeamModal.logoHint")}
                                        accept="image/*"
                                        value={logoFile}
                                        previewUrl={logoPreviewUrl}
                                        onChangeFile={(file) => setLogoFile(file)}
                                        onClear={clearLogo}
                                    />

                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label style={modalStyles.label}>{t("editTeamModal.nameLabel")}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            required
                                            name="name"
                                            id="edit-org-name"
                                            placeholder={t("editTeamModal.namePlaceholder")}
                                            style={modalStyles.input}
                                        />
                                        <div style={modalStyles.hint}>
                                            {t("editTeamModal.nameHint")}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={() => {
                                        clearLogo();
                                        hideModal();
                                    }}
                                    style={{ ...modalStyles.btn, ...modalStyles.btnGhost }}
                                >
                                    <i className="fas fa-times" />
                                    {t("editTeamModal.close")}
                                </button>

                                <button
                                    type="submit"
                                    className="btn"
                                    style={{ ...modalStyles.btn, ...modalStyles.btnPrimary }}
                                >
                                    <i className="fas fa-check" />
                                    {t("editTeamModal.save")}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
        #edit-team-modal.modal {
          background: rgba(0, 0, 0, 0.45);
          backdrop-filter: blur(2px);
        }

        #edit-team-modal .modal-dialog {
          max-width: 720px;
          width: calc(100% - 24px);
          margin: 1.25rem auto;
        }

        #edit-team-modal .form-control:focus {
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9);
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }

        #edit-team-modal {
          --kbola-canary: ${GlobalStyles.colors.canary};
          --kbola-woodsmoke: ${GlobalStyles.colors.woodsmoke};
        }

        #edit-team-modal .kbola-file-native{
          position:absolute;
          width:1px;height:1px;
          padding:0;margin:-1px;
          overflow:hidden;
          clip: rect(0,0,0,0);
          white-space:nowrap;
          border:0;
        }
        #edit-team-modal .kbola-file-shell{
          display:flex;
          align-items:center;
          gap:10px;
          border-radius: 14px;
          border: 1.5px dashed rgba(20,21,26,0.20);
          background: rgba(255,255,255,0.85);
          padding: 10px 10px;
          transition: border-color .12s ease, box-shadow .12s ease, transform .12s ease;
        }
        #edit-team-modal .kbola-file-shell:hover{
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.85);
          box-shadow: 0 12px 26px rgba(0,0,0,0.06);
          transform: translateY(-1px);
        }
        #edit-team-modal .kbola-file-shell:focus-within{
          border-color: var(--kbola-canary);
          box-shadow: 0 0 0 3px rgba(var(--kbs-primary-rgb, 0,173,255), 0.26);
        }
        #edit-team-modal .kbola-file-btn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:8px;
          padding: 10px 12px;
          border-radius: 12px;
          background: var(--kbola-woodsmoke);
          color: var(--kbola-canary);
          font-weight: 900;
          cursor:pointer;
          margin:0;
          user-select:none;
          white-space:nowrap;
          border: 1px solid rgba(20,21,26,0.15);
        }
        #edit-team-modal .kbola-file-meta{
          flex:1;
          min-width:0;
        }
        #edit-team-modal .kbola-file-name{
          display:inline-block;
          max-width: 100%;
          overflow:hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 800;
          color: var(--kbola-woodsmoke);
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
          border: 1px solid rgba(var(--kbs-primary-rgb, 0,173,255), 0.35);
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
        }
        #edit-team-modal .kbola-file-placeholder{
          color: rgba(20,21,26,0.45);
          font-weight: 700;
          font-size: 12px;
        }
        #edit-team-modal .kbola-file-subhint{
          margin-top: 4px;
          font-size: 11px;
          color: rgba(20,21,26,0.48);
          font-weight: 700;
        }
        #edit-team-modal .kbola-file-clear{
          width: 38px;
          height: 38px;
          border-radius: 12px;
          border: 1px solid rgba(20,21,26,0.10);
          background: rgba(255,255,255,0.9);
          color: var(--kbola-woodsmoke);
          display:flex;
          align-items:center;
          justify-content:center;
          cursor:pointer;
        }
        #edit-team-modal .kbola-file-preview{
          display:flex;
          align-items:center;
          gap:12px;
          margin-top: 10px;
        }
        #edit-team-modal .kbola-file-thumb{
          width: 72px;
          height: 72px;
          border-radius: 14px;
          overflow:hidden;
          border: 1px solid rgba(20,21,26,0.10);
          box-shadow: 0 12px 24px rgba(0,0,0,0.06);
          background:#fff;
        }
        #edit-team-modal .kbola-file-thumb img{
          width:100%;
          height:100%;
          object-fit: cover;
          display:block;
        }
        #edit-team-modal .kbola-label{
          font-size: 12.5px;
          font-weight: 800;
          color: rgba(20,21,26,0.70);
          margin-bottom: 6px;
          display:block;
        }

        @media (max-width: 768px) {
          #edit-team-modal .modal-dialog {
            max-width: 95%;
            width: 95%;
            margin: 0.9rem auto;
          }
        }
      `}</style>
        </>
    );
}
